import CustomButton from "@Common/CustomButton";
import CustomPhoneNumberInput from "@Common/customPhoneNumberInput";
import Input from "@Common/input";
import { Grid } from "@mui/material";
import { FORM_REGEX_VALIDATORS } from "@utils";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePostRegister } from "@services";
import { useNavigate } from "react-router-dom";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import { useContext, useEffect, useState } from "react";
import { LoadingWrapper } from "@components";
import RadioSection from "./RadioSection";
import MsoolLoader from "./MsoolLoader";
import { maskEmail } from "../../../../../../src/utils/mailUtil";
interface IForm {
  FullName: string;
  Email: string;
  PhoneNumber: string;
  numberOfProperty: string;
  Password: string;
  confirm_password: string;
  userType: string;
  city: string;
  channels: number[];
  isAgreeTerms: boolean;
  ReferralCode?: string;
}

interface IResponseRegister {
  result: {
    email: string;
  };
}

export default function FormInputs() {
  const { showError, showSuccess } = useContext(ToastMessageContext);
  const [requirementLowercase, setRequirementLowercase] = useState(false);
  const [requirementMinLength, setRequirementMinLength] = useState(false);
  const [requirementNumber, setRequirementNumber] = useState(false);
  const [requirementSpecialChar, setRequirementSpecialChar] = useState(false);
  const [requirementUppercase, setRequirementUppercase] = useState(false);
  const [registeredMail, setRegisteredMail] = useState<string>("");
  const [showInvitationCode, setShowInvitationCode] = useState(false);

  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const methods = useForm<IForm>({
    mode: "onChange",
    defaultValues: {
      userType: "Default",
      city: "Default",
      channels: [0],
      isAgreeTerms: true,
      ReferralCode: "",
    },
  });
  const { mutateAsync: mutateAsyncUsePostRegister, isPending } =
    usePostRegister();
  const { watch, reset } = methods;
  const passwordWatch = watch("Password");
  const emailWatch = watch("Email");
  const invitationCodeWatch = watch("ReferralCode");
  const onSubmit = async (data: IForm) => {
    // Validate invitation code only if the input is shown
    if (
      showInvitationCode &&
      (!data.ReferralCode || data.ReferralCode.trim().length < 3)
    ) {
      showError(t("Signup.invitationCodeRequired"));
      return;
    }

    const response = await mutateAsyncUsePostRegister({ ...data });
    if (response.status === 200) {
      reset();
      navigate(`/register/otp/${data?.Email}`);
    }
  };
  useEffect(() => {
    const lowercaseRegex = /[a-z]/;
    const uppercaseRegex = /[A-Z]/;
    const numberRegex = /[0-9]/;
    const specialCharRegex = /[^A-Za-z0-9]/;
    const minLength = 8;

    setRequirementLowercase(lowercaseRegex.test(passwordWatch));
    setRequirementUppercase(uppercaseRegex.test(passwordWatch));
    setRequirementNumber(numberRegex.test(passwordWatch));
    setRequirementSpecialChar(specialCharRegex.test(passwordWatch));
    setRequirementMinLength(passwordWatch?.length >= minLength);
  }, [passwordWatch]); // ✅ Triggers on every password change
  if (isPending) {
    return <MsoolLoader Text={t("Signup.signupLoading")} />;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container>
            <Grid item xs={12}>
              <Input
                name="FullName"
                label={t("Signup.name")}
                placeholder={t("Signup.name")}
                registerOptions={{
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                name="Email"
                label={t("Signup.email")}
                placeholder={t("Signup.email")}
                registerOptions={{
                  pattern: {
                    value: FORM_REGEX_VALIDATORS.email,
                    message: t("COMMON.REQUIRED"),
                  },
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                name="PhoneNumber"
                label={t("ADD_PROPERTY.PHONE_NUMBER")}
                placeholder={t("ADD_PROPERTY.PHONE_NUMBER")}
                registerOptions={{
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                  pattern: {
                    value: /^05\d{8}$/, // ✅ Starts with 05 and has 10 digits
                    message: t("Signup.INVALID_PHONE"), // e.g., "Phone number must start with 05 and be 10 digits long"
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                name="Password"
                label={t("Signup.PASSWORD")}
                placeholder={t("Signup.PASSWORD")}
                type="password"
                registerOptions={{
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                  minLength: {
                    value: 8,
                    message: t(
                      "Signup.password_must_be_at_least_8_characters_long"
                    ),
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <RadioSection
                requirementLowercase={requirementLowercase}
                requirementMinLength={requirementMinLength}
                requirementNumber={requirementNumber}
                requirementSpecialChar={requirementSpecialChar}
                requirementUppercase={requirementUppercase}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                name="confirm_password"
                label={t("Signup.Confirm_PASSWORD")}
                placeholder={t("Signup.Confirm_PASSWORD")}
                type="password"
                registerOptions={{
                  validate: (value) =>
                    value === passwordWatch || t("Signup.PASSWORD_ERROR"),
                  required: {
                    value: true,
                    message: t("COMMON.REQUIRED"),
                  },
                }}
              />
            </Grid>

            {/* Do u have invitation Code  */}
            <span
              onClick={() => {
                setShowInvitationCode((prev) => !prev);
              }}
              style={{
                fontSize: "14px",
                fontWeight: "700",
                color: "#5B3FFF",
                marginBottom: "10px",
                cursor: "pointer",
              }}
            >
              {t("Signup.invitationCodeQuestion")}
            </span>
            {showInvitationCode && (
              <Grid item xs={12}>
                <Input
                  name="ReferralCode"
                  placeholder={t("Signup.invitationCodePlaceholder")}
                  type="text"
                  registerOptions={{
                    validate: (value) => {
                      if (!value || value.trim().length === 0) {
                        return t("Signup.invitationCodeRequired");
                      }
                      if (value.trim().length < 3) {
                        return t("Signup.invitationCodeMinLength");
                      }
                      if (!/^[A-Za-z0-9]+$/.test(value)) {
                        return t("Signup.invitationCodeInvalid");
                      }
                      return true;
                    },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <CustomButton
                style={{ width: "100%", borderRadius: "69px" }}
                withBG
                label={t("Signup.signup")}
                customPadding="16px"
                borderRadius="8px"
                type="submit"
              />
            </Grid>
            <Grid item xs={12}>
              <div className="HAVE_ACCOUNT" onClick={() => navigate("/login")}>
                <p>
                  {t("SUBSCRIPTION.HAVE_ACCOUNT")}
                  <span>{t("SUBSCRIPTION.LOGIN")}</span>
                </p>
              </div>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </div>
  );
}
